import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User } from '../../features/auth/types/auth.types';
import { localStorageService } from '@/services/localStorage.service';
import { notificationManager } from '@/services/notificationManager.service';
import { v4 as uuidv4 } from 'uuid';
import { authApi } from '@/features/auth/services/auth.service';
import { queryClient } from './QueryProvider';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  visitorId: string;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  logout: () => Promise<void>;
  initializeAuth: (chatbotId?: string | null) => void;
  initializeAuthAsync: (chatbotId?: string | null) => Promise<void>;
  updateCount: (
    key: keyof User['usage'],
    value: number
  ) => void;
  decrementServiceMessageCount: (amount?: number) => void;
}


const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [visitorId, setVisitorId] = useState<string>('');

  const generateVisitorId = () => {
    const newVisitorId = `visitor-${uuidv4()}`;
    const expiry = Date.now() + 60 * 60 * 1000;
    localStorage.setItem('visitorId', newVisitorId);
    localStorage.setItem('visitorIdExpiry', expiry.toString());
    setVisitorId(newVisitorId);
    return newVisitorId;
  };

  const getVisitorId = () => {
    const storedId = localStorage.getItem('visitorId');
    const expiry = localStorage.getItem('visitorIdExpiry');
    if (storedId && expiry && Date.now() < parseInt(expiry)) {
      setVisitorId(storedId);
      return storedId;
    }
    return generateVisitorId();
  };



  const initializeAuth = useCallback((chatbotId?: string | null) => {
    setTimeout(async () => {
      const effectiveChatbotId = chatbotId || localStorageService.getChatbotId() || '';
      if (effectiveChatbotId) {
        try {
          const currentUser = await authApi.getMe(effectiveChatbotId);
          setUser(currentUser.data);
          // Persist the effective chatbotId to localStorage so useChatbotId() always
          // has localStorage as the authoritative source.
          // - If chatbotId was explicitly passed (e.g. chatbot switch), always overwrite.
          // - If not passed, only write if localStorage is currently empty (first login).
          if (chatbotId || !localStorageService.getChatbotId()) {
            localStorageService.storeChatbotId(effectiveChatbotId);
          }
          // Initialize notifications for logged-in user
          if (currentUser.data?.user.id) {
            await notificationManager.onUserLogin(currentUser.data.user.id);
          }
        } catch (error: any) {
          console.error('Failed to fetch user:', error);

          // If getMe fails during a remote access session (e.g. expired/invalid token),
          // clear all state and redirect to login with a human-readable message instead
          // of leaving the user on a blank/broken screen with no feedback.
          const isRemoteAccess = localStorage.getItem('isRemoteAccess');
          if (isRemoteAccess === 'true') {
            const message =
              error?.response?.data?.message || 'Remote access session expired';
            await localStorageService.clearAll();
            window.location.href = `/login?message=${encodeURIComponent(message)}`;
            return;
          }
        }
      }
      getVisitorId();
      setIsLoading(false);
    }, 100);
  }, []);

  const initializeAuthAsync = useCallback(async(chatbotId?: string | null) => {
      const effectiveChatbotId = chatbotId || localStorageService.getChatbotId() || '';
      if (effectiveChatbotId) {
        try {
          const currentUser = await authApi.getMe(effectiveChatbotId);
          setUser(currentUser.data);
          // Persist the effective chatbotId to localStorage so useChatbotId() always
          // has localStorage as the authoritative source.
          // - If chatbotId was explicitly passed (e.g. chatbot switch), always overwrite.
          // - If not passed, only write if localStorage is currently empty (first login).
          if (chatbotId || !localStorageService.getChatbotId()) {
            localStorageService.storeChatbotId(effectiveChatbotId);
          }
          // Initialize notifications for logged-in user
          if (currentUser.data?.user.id) {
            await notificationManager.onUserLogin(currentUser.data.user.id);
          }
          return currentUser.data;
        } catch (error) {
          console.error('Failed to fetch user:', error);
        }
      }
      getVisitorId();
      setIsLoading(false);
      return null;
  }, []);



  useEffect(() => {
    const pathname = window.location.pathname;
    const parts = pathname.split('/');
    // Only treat the 3rd URL segment as a chatbotId if it's a valid 24-char hex ObjectId
    const segmentId = parts[2];
    const isValidObjectId = segmentId && /^[0-9a-fA-F]{24}$/.test(segmentId);
    const botId = (isValidObjectId ? segmentId : null) || localStorageService.getChatbotId() || '';

    initializeAuth(botId);

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user') {
        window.location.reload();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const logout = async () => {
    try {
      // Clean up notifications before logout
      await notificationManager.onUserLogout();

      // Call backend to revoke refresh token
      await authApi.logout();
    } catch (error) {
      console.error('Error during logout:', error);
      // Continue with local cleanup even if API call fails
    } finally {
      // Clear local storage and state
      await localStorageService.clearAll();
      setUser(null);
      setTimeout(queryClient.clear, 100);
    }
  };

  const updateCount = (
    key: keyof User['usage'],
    value: number
  ) => {
    setUser((prev) => {
      if (!prev) return prev;

      const limitKeyMap: Record<keyof User['usage'], keyof User['limits']> = {
        aiCreditsUsed: 'aiCredits',
        contactsLimitUsed: 'contactsLimit',
        tagsLimitUsed: 'tagsLimit',
        customAttributesLimitUsed: 'customAttributesLimit',
        automationsLimitUsed: 'automationsLimit',
        agentsLimitUsed: 'agentsLimit',
        appointmentUsersLimitUsed: 'appointmentUsersLimit',
        dripCampaignsLimitUsed: 'dripCampaignsLimit',
      };

      const limitKey = limitKeyMap[key];
      const maxLimit = prev.limits[limitKey];

      let safeValue = value;

      if (safeValue < 0) safeValue = 0;

      if (safeValue > maxLimit) safeValue = maxLimit;

      if (prev.usage[key] === safeValue) return prev;

      return {
        ...prev,
        usage: {
          ...prev.usage,
          [key]: safeValue,
        },
      };
    });
  };

  const decrementServiceMessageCount = useCallback((amount: number = 1) => {
    setUser((prev) => {
      if (!prev) return prev;
      const currentUsage = prev.serviceMessageUsage;
      const freeLimit = currentUsage?.freeLimit ?? 1000;
      const usedCount = (currentUsage?.usedCount ?? 0) + amount;
      const remaining = Math.max(0, freeLimit - usedCount);

      return {
        ...prev,
        serviceMessageUsage: {
          freeLimit,
          usedCount,
          remaining,
          monthKey: currentUsage?.monthKey || new Date().toISOString().slice(0, 7),
        },
      };
    });
  }, []);

  useEffect(() => {
    const handleServiceMessageSent = () => {
      decrementServiceMessageCount(1);
    };

    window.addEventListener('whatsapp-service-message-sent', handleServiceMessageSent);
    return () => {
      window.removeEventListener('whatsapp-service-message-sent', handleServiceMessageSent);
    };
  }, [decrementServiceMessageCount]);

  const value: AuthContextType = {
    user,
    isLoading,
    visitorId,
    setUser,
    setIsLoading,
    logout,
    initializeAuth,
    initializeAuthAsync,
    updateCount,
    decrementServiceMessageCount,
  };


  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}